/*!
 * mm-plan.js — the DECISIONS of a mashup: at what tempo it plays, which song
 * moves in pitch and by how much, which part of the singer's song is used and
 * on which bar of the instrumental it lands. Pure functions over numbers the
 * analysis already produced (beat grids, per-frame chroma, per-frame level);
 * no audio, no DOM, so every decision is testable under node.
 *
 * The rules, and where each one comes from:
 *
 *  • Tempo — one A-beat is mapped onto k B-beats, k ∈ {½, 1, 2}, whichever
 *    keeps the stretch closest to 1. Beat trackers pick the half/double level
 *    freely (Beat This! read the tagged-100 BPM hip-hop as 200 and the tagged-120
 *    trip-hop as 60 on our corpus), so a "140 vs 70 BPM" pair is the SAME tempo.
 *
 *  • Pitch — chosen by MEASURED harmonic agreement, not by key names: key labels
 *    are wrong ~19 % of the time even for the best CNN (fifth/relative/parallel
 *    errors, GiantSteps). AutoMashUpper's rule (Davies et al., ISMIR 2013):
 *    beat-synchronous chroma of the vocal, rotated by r semitones, against the
 *    instrumental's chroma on the beats it will actually sit on; cosine, argmax.
 *
 *  • Who moves — a vocal pitch-shifted more than ~2 semitones starts to sound
 *    processed even with formant compensation (DJ TechTools; Signalsmith's own
 *    README calls its formant correction softer than PSOLA). Past that the rest
 *    goes to the instrumental's PITCHED stems (bass + other) — never the drums.
 *
 *  • Where — bars, not seconds: the vocal region starts on the downbeat of the
 *    bar that holds the first sung onset, so a pickup (singers routinely come in
 *    a beat or two early) travels with its bar instead of being pulled onto the
 *    downbeat. Candidate landing bars get a bonus on 4-bar phrase starts and where
 *    the instrumental's level rises (the verse after an intro); over music with no
 *    singer of its own the landing leans to the beginning (see `early` in plan()).
 *
 *  • What is HEARD of the vocal — `plan.extent`, from its first sound to its last
 *    (vocalExtent): the ends stand in the silence around the singing, and a file that
 *    IS the vocal is heard whole. The bars decide where it lands, never what is cut.
 *
 *   MashupPlan.plan(A, B, opts) → plan (see bottom of the file)
 *   MashupPlan.planSecond(planA, C, B, opts) → the second singer's plan over the same music
 *   MashupPlan.pairFit({bpm, meter, key}, {bpm, meter, key}) → the pair verdict (deck strip + pair finder)
 *   MashupPlan.keyOf(chroma12)  → { tonic, mode, camelot, name, confidence }
 *   MashupPlan.vocalPhrases(rms, frameSec) → [{t0,t1}]
 *   MashupPlan.soundSpan(rms, frameSec) → {t0,t1} | null — where a track sounds at all
 *   MashupPlan.vocalExtent(A, barsA, a0, a1, full) → {t0,t1,cut0,cut1} — what is heard for bars [a0,a1)
 *   MashupPlan.warpAnchors(plan, A, outBeats, barsB) → [[aSec, outSec], …] from extent.t0 to extent.t1
 *   MashupPlan.musicLoop(grid, duration, o, needBeat, {level, slack}) → how the music goes round under a longer vocal
 */
(function (root) {
    'use strict';

    var NOTE = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
    // Camelot numbers per tonic (0 = C) — major = B, minor = A
    var CAM_MAJ = [8, 3, 10, 5, 12, 7, 2, 9, 4, 11, 6, 1];
    var CAM_MIN = [5, 12, 7, 2, 9, 4, 11, 6, 1, 8, 3, 10];
    // Krumhansl–Kessler and Temperley key profiles (index 0 = tonic)
    var KK_MAJ = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
    var KK_MIN = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];
    var TP_MAJ = [5.0, 2.0, 3.5, 2.0, 4.5, 4.0, 2.0, 4.5, 2.0, 3.5, 1.5, 4.0];
    var TP_MIN = [5.0, 2.0, 3.5, 4.5, 2.0, 4.0, 2.0, 4.5, 3.5, 2.0, 1.5, 4.0];

    function pearson(a, b) {
        var ma = 0, mb = 0, i;
        for (i = 0; i < 12; i++) { ma += a[i]; mb += b[i]; }
        ma /= 12; mb /= 12;
        var n = 0, da = 0, db = 0;
        for (i = 0; i < 12; i++) { var x = a[i] - ma, y = b[i] - mb; n += x * y; da += x * x; db += y * y; }
        return (da && db) ? n / Math.sqrt(da * db) : 0;
    }

    /** Key of a 12-bin chroma (profile ensemble). */
    function keyOf(ch) {
        var best = null, second = -2;
        for (var t = 0; t < 12; t++) {
            var rot = new Float64Array(12);
            for (var p = 0; p < 12; p++) rot[p] = ch[(p + t) % 12];
            var sMaj = 0.5 * pearson(rot, KK_MAJ) + 0.5 * pearson(rot, TP_MAJ);
            var sMin = 0.5 * pearson(rot, KK_MIN) + 0.5 * pearson(rot, TP_MIN);
            [[sMaj, 'major'], [sMin, 'minor']].forEach(function (c) {
                if (!best || c[0] > best.score) { if (best) second = Math.max(second, best.score); best = { score: c[0], tonic: t, mode: c[1] }; }
                else if (c[0] > second) second = c[0];
            });
        }
        var cam = best.mode === 'major' ? CAM_MAJ[best.tonic] + 'B' : CAM_MIN[best.tonic] + 'A';
        return {
            tonic: best.tonic, mode: best.mode, camelot: cam,
            name: NOTE[best.tonic] + (best.mode === 'minor' ? 'm' : ''),
            confidence: Math.max(0, Math.min(1, (best.score - second) * 6 + best.score * 0.3))
        };
    }

    /** Camelot relation between two keys → 'same' | 'relative' | 'adjacent' | 'energy' | 'far'. */
    function camelotRelation(ka, kb) {
        var na = parseInt(ka.camelot, 10), nb = parseInt(kb.camelot, 10);
        var la = ka.camelot.slice(-1), lb = kb.camelot.slice(-1);
        var d = Math.min((na - nb + 12) % 12, (nb - na + 12) % 12);
        if (d === 0 && la === lb) return 'same';
        if (d === 0) return 'relative';
        if (d === 1 && la === lb) return 'adjacent';
        if (d === 2 && la === lb) return 'energy';
        return 'far';
    }

    // «Is there a between-the-lines»: how deep the level HELD over GAP_HOLD_S must fall (its 10th
    // percentile under the loud level) for the floor rule of vocalPhrases to apply. Swept 14.10.2026
    // over 19 separated song vocals and 35 real a cappella takes: at 0.5 s / 15 dB every song's phrases
    // stay exactly what they were (the shallowest beds sit at 17.1 and 17.8) and the takes sung through
    // are whole; 18 dB starts merging songs (a looped line over a pad: 23 phrases → 1), 10–12 dB still
    // leaves takes cut.
    var GAP_MIN_DB = 15, GAP_HOLD_S = 0.5;
    /** The 10th percentile of `a`. */
    function p10(a) { var s = Array.prototype.slice.call(a).sort(function (x, y) { return x - y; }); return s[Math.floor(s.length * 0.1)]; }

    /**
     * Sung phrases from a frame RMS track (linear): the level against the track's own loud level
     * (95th percentile) — open −24 dB / close −34 dB — and, where the track has pauses between its
     * lines, against what sounds in them (10th percentile + 12 / + 6 dB); −70 dBFS is the only
     * absolute floor. Hold 200 ms, gaps < 350 ms merged, phrases ≥ 0.5 s.
     */
    function vocalPhrases(rms, frameSec) {
        var n = rms.length, db = new Float32Array(n), vals = [], i, first = -1, lastOn = -1;
        for (i = 0; i < n; i++) {
            db[i] = 20 * Math.log10(rms[i] + 1e-9);
            if (rms[i] > 1e-5) { vals.push(db[i]); if (first < 0) first = i; lastOn = i; }
        }
        if (!vals.length) return [];
        vals.sort(function (a, b) { return a - b; });
        var ref = vals[Math.floor(vals.length * 0.95)];
        // relative to the stem's own loudness: a quietly mixed ambient vocal (p95 -45 dBFS on
        // the corpus) is still a vocal; -70 dBFS is the only absolute floor (digital silence)
        // ...and relative to its own NOISE FLOOR (10th percentile): a stem that never
        // falls silent between lines (backing vocals, separation leftovers — the rap test
        // song merged its first 23 s into one "phrase") still splits where the voice stops.
        // The floor is taken over the stem's INTERIOR with digital silence counted as
        // -100 dB: a studio a cappella or a gated stem is exactly zero between lines, and
        // counting only the sounding frames made the singing level itself the "floor" —
        // no phrase ever opened (0 phrases on a clean 16-bar vocal, 23.09.2026).
        var inner = new Float32Array(lastOn - first + 1);
        for (i = first; i <= lastOn; i++) inner[i - first] = rms[i] > 1e-5 ? db[i] : -100;
        var floorDb = p10(inner);
        // ...but a 10th percentile is a FLOOR only where the take has a «between the lines» at all.
        // A take sung through — a hummed verse of 15 s, a vocal after a compressor and a reverb —
        // has none: its 10th percentile is the singing itself, and floor + 12 landed at or above
        // the loud level. Report 2026-10-13 («акапелла открывается в укороченном виде, с середины
        // пятого слова и заканчивается посередине слова»): on the reporter's own vocal (p10 −19.9,
        // p95 −10.5 dBFS) a phrase had to pass −7.9 dB to open — none did, and the build answered
        // «почти нет пения»; a take a few dB away opens on its loudest syllable alone and closes
        // mid-word, and the vocal's place in the mashup was cut from that. 35 takes of real
        // a cappellas, 15–18 s each: 10 refused, 3 more kept 34–62 % of the take.
        // Whether there IS a between is read off the level HELD over half a second (the loudest
        // frame of every 0.5 s): a consonant or a breath disappears in it, a pause between lines
        // stays. Its 10th percentile says how far the take falls when it falls for that long, a
        // tenth of its length. Measured (dB under the loud level): takes sung through 0–8, with a
        // few short breaths 10–14; stems with a bed between the lines 17–57 — the rap stem the rule
        // was made for 25, a looped line over a steady bed 17. Under GAP_MIN_DB there is nothing
        // to stay above, and the thresholds are the loud level's alone.
        var w = Math.max(1, Math.round(GAP_HOLD_S / 2 / frameSec)), held = new Float32Array(inner.length), j, m;
        for (i = 0; i < inner.length; i++) {
            m = -200;
            for (j = Math.max(0, i - w); j <= i + w && j < inner.length; j++) if (inner[j] > m) m = inner[j];
            held[i] = m;
        }
        var gaps = ref - p10(held) >= GAP_MIN_DB;
        var open = Math.max(ref - 24, gaps ? floorDb + 12 : -200, -70), close = Math.max(ref - 34, gaps ? floorDb + 6 : -200, -76);
        if (close > open - 4) close = open - 4;
        var hold = Math.round(0.2 / frameSec), on = false, last = -1, segs = [], start = 0;
        for (i = 0; i < n; i++) {
            if (!on && db[i] > open) { on = true; start = i; last = i; }
            else if (on) {
                if (db[i] > close) last = i;
                else if (i - last > hold) { segs.push([start, last]); on = false; }
            }
        }
        if (on) segs.push([start, last]);
        var merged = [];
        for (i = 0; i < segs.length; i++) {
            var s = segs[i];
            if (merged.length && (s[0] - merged[merged.length - 1][1]) * frameSec < 0.35) merged[merged.length - 1][1] = s[1];
            else merged.push([s[0], s[1]]);
        }
        return merged.filter(function (s) { return (s[1] - s[0]) * frameSec >= 0.5; })
            .map(function (s) { return { t0: s[0] * frameSec, t1: (s[1] + 1) * frameSec }; });
    }

    /**
     * Where a take SOUNDS at all — { t0, t1 } from its first to its last frame within 40 dB of its
     * loud level (and over −70 dBFS); null for silence. Not «where the singing is» — that is
     * vocalPhrases, a judgement — but what a file the person calls an a cappella holds between the
     * silence at its ends: a soft first word, a breath, the tail of a reverb all count.
     * `sung0` — where it first sounds like singing: the start of the first 0.3 s that stay, for the
     * most part, within 34 dB of the loud level (the level a phrase stays open at). The hiss or the
     * click a file holds before that is kept in the track but need not be heard; what is that loud
     * must be — the landing and the mashup's start are counted from it (plan(), leadOf), whatever
     * the phrase detector made of the first line.
     */
    function soundSpan(rms, frameSec) {
        var n = rms.length, vals = [], db = new Float32Array(n), i;
        for (i = 0; i < n; i++) { db[i] = 20 * Math.log10(rms[i] + 1e-9); if (rms[i] > 1e-5) vals.push(db[i]); }
        if (!vals.length) return null;
        vals.sort(function (a, b) { return a - b; });
        var ref = vals[Math.floor(vals.length * 0.95)], thr = Math.max(ref - 40, -70), first = -1, last = -1;
        for (i = 0; i < n; i++) if (db[i] > thr) { if (first < 0) first = i; last = i; }
        if (first < 0) return null;
        var w = Math.max(1, Math.round(0.3 / frameSec)), need = Math.ceil(w * 0.6), cnt = 0, sung = -1, lthr = Math.max(ref - 34, -70);
        for (i = first; i < n; i++) {
            if (db[i] > lthr) cnt++;
            if (i - w >= first && db[i - w] > lthr) cnt--;
            if (cnt >= need) { sung = Math.max(first, i - w + 1); while (sung < i && db[sung] <= lthr) sung++; break; }
        }
        return { t0: first * frameSec, t1: (last + 1) * frameSec, sung0: sung < 0 ? null : sung * frameSec };
    }

    /**
     * Where the singer first SINGS, in the track's own seconds (null — nowhere): the onset of the
     * first phrase — or, in a file that IS the vocal, where it first sounds like singing (soundSpan
     * `sung0`), whichever comes first. The phrase detector is a judgement and may open late on a soft
     * first line; in a file the person calls an a cappella nothing that loud is anything else.
     */
    function sungFrom(A) {
        var ph = A.phrases || [], t = ph.length ? ph[0].t0 : Infinity;
        if (A.whole && A.sound && A.sound.sung0 != null) t = Math.min(t, A.sound.sung0);
        return isFinite(t) ? t : null;
    }

    /**
     * What of the singer's track is HEARD for bars [a0, a1) — { t0, t1 } in the track's own seconds,
     * `cut0` / `cut1` when an end falls inside a phrase (the engine fades there, not clicks).
     *
     * The ends stand in the silence around the singing, never a fixed count of bars or seconds from
     * the region's lines. Until 2026-10-14 the vocal was cut one bar before bar a0 and three seconds
     * after its last counted beat, whatever sounded there: a singer still mid-line a bar before the
     * region started mid-word, and a model grid that ended before the singing did cut the ending —
     * 10.7 s of the last verse of a 4.5-min corpus song — with the sound after it riding unwarped.
     *
     *  • the WHOLE vocal of a file that IS the vocal (`A.whole` — «this is an a cappella»): all the
     *    file holds between the silence at its ends (soundSpan). The person gave it; the phrase
     *    detector's opinion decides where it LANDS, not what of it is kept.
     *  • otherwise, at the head: one bar of lead-in before bar a0 (a pickup lives there). A line
     *    that began before the lead-in and is caught by it half-way is the previous line: the vocal
     *    starts after its end — or, when that line runs on deep into the region (the fragment was
     *    asked to start inside it), on the region's own bar line, faded in;
     *  • at the tail: the end of the last line begun in the region. The whole vocal lets it finish;
     *    a chosen fragment lets it run up to one bar past its line and is cut on the line otherwise
     *    (a take sung through is ONE phrase — «8 bars» of it is 8 bars).
     */
    var PAD_IN = 0.15, PAD_OUT = 0.5, CUT_FADE = 0.08;
    function vocalExtent(A, barsA, a0, a1, full) {
        var dur = A.duration, ph = A.phrases || [], nb = barsA.length, i;
        var barSec = nb > 1 ? (barsA[nb - 1].t - barsA[0].t) / (nb - 1) : 2;
        if (full && A.whole && A.sound) {
            var w0 = Math.max(0, A.sound.t0 - PAD_IN), w1 = Math.min(dur, A.sound.t1 + PAD_OUT);
            return w1 > w0 + 0.2 ? { t0: w0, t1: w1, cut0: false, cut1: false } : { t0: 0, t1: dur, cut0: false, cut1: false };
        }
        var r0 = barsA[Math.min(a0, nb - 1)].t, r1 = a1 < nb ? barsA[a1].t : dur;
        var z = null, next = null;
        for (i = 0; i < ph.length; i++) {
            if (ph[i].t0 < r1) z = ph[i];                            // the last line begun in the region
            else if (!next) next = ph[i];                             // the line after it
        }
        var t0 = r0 - barSec, t1, cut0 = false, cut1 = false;
        for (i = 0; i < ph.length; i++) if (ph[i].t0 < t0 && ph[i].t1 > t0) {
            if (ph[i].t1 > r0 + barSec) { t0 = r0 - CUT_FADE; cut0 = true; }
            else t0 = ph[i].t1 + 0.05;
            break;
        }
        if (!z || z.t1 <= r0) t1 = r1;
        else if (z.t1 <= r1 || full || z.t1 - r1 <= barSec) t1 = z.t1 + PAD_OUT;
        else { t1 = r1 + CUT_FADE; cut1 = true; }
        if (!cut1 && next && next.t0 - 0.03 < t1) t1 = Math.max(z ? z.t1 : r1, next.t0 - 0.03);   // the next line begins inside the release
        t0 = Math.max(0, t0); t1 = Math.min(dur, t1);
        if (!(t1 > t0 + 0.2)) { t0 = Math.max(0, r0 - barSec); t1 = Math.min(dur, Math.max(r1, t0 + 0.5)); cut0 = cut1 = false; }
        return { t0: t0, t1: t1, cut0: cut0, cut1: cut1 };
    }

    /** Time on a beat grid at fractional beat index x (linear inside, extrapolated at the ends). */
    function timeAtBeat(beats, x) {
        var n = beats.length;
        if (!n) return 0;
        if (n === 1) return beats[0];
        if (x <= 0) return beats[0] + x * (beats[1] - beats[0]);
        if (x >= n - 1) return beats[n - 1] + (x - (n - 1)) * (beats[n - 1] - beats[n - 2]);
        var i = Math.floor(x), f = x - i;
        return beats[i] + f * (beats[i + 1] - beats[i]);
    }
    /** Fractional beat index of time t. */
    function beatAtTime(beats, t) {
        var n = beats.length;
        if (n < 2) return 0;
        if (t <= beats[0]) return (t - beats[0]) / (beats[1] - beats[0]);
        if (t >= beats[n - 1]) return (n - 1) + (t - beats[n - 1]) / (beats[n - 1] - beats[n - 2]);
        var lo = 0, hi = n - 1;
        while (hi - lo > 1) { var m = (lo + hi) >> 1; if (beats[m] <= t) lo = m; else hi = m; }
        return lo + (t - beats[lo]) / (beats[hi] - beats[lo]);
    }

    /** Mean chroma (and weight) of frames inside [t0,t1). frames: {data, times} (F×12). */
    function chromaIn(fr, t0, t1, out) {
        for (var d = 0; d < 12; d++) out[d] = 0;
        var times = fr.times, F = times.length, cnt = 0;
        var lo = 0, hi = F;
        while (lo < hi) { var m = (lo + hi) >> 1; if (times[m] < t0) lo = m + 1; else hi = m; }
        for (var f = lo; f < F && times[f] < t1; f++) { for (d = 0; d < 12; d++) out[d] += fr.data[f * 12 + d]; cnt++; }
        if (!cnt && F) {
            var near = Math.min(F - 1, lo);
            for (d = 0; d < 12; d++) out[d] = fr.data[near * 12 + d];
            cnt = 1;
        }
        return cnt;
    }

    function norm12(v) {
        var s = 0; for (var i = 0; i < 12; i++) s += v[i] * v[i];
        s = Math.sqrt(s);
        if (s > 0) for (i = 0; i < 12; i++) v[i] /= s;
        return s;
    }

    /**
     * How a sung pitch class sounds over a sounding one, by interval (semitones
     * above the accompaniment note). Unison/octave and the perfect fifth/fourth
     * blend, thirds and sixths are the chord tones a melody lives on, seconds and
     * sevenths rub, the minor second / major seventh clash. Values are relative;
     * only their ORDER matters to the argmax, and the self-mashup test pins it.
     */
    var CONSONANCE = [1.0, -0.9, 0.1, 0.65, 0.75, 0.55, -0.45, 0.85, 0.6, 0.65, 0.05, -0.9];

    /** Consonance field of an accompaniment chroma (L1-normalised): F[p] = sum_q c[q]*K[(p-q) mod 12]. */
    function field(c, out) {
        var s = 0, q, p;
        for (q = 0; q < 12; q++) s += c[q];
        for (p = 0; p < 12; p++) out[p] = 0;
        if (s <= 0) return 0;
        for (p = 0; p < 12; p++) {
            var acc = 0;
            for (q = 0; q < 12; q++) acc += c[q] * CONSONANCE[((p - q) % 12 + 12) % 12];
            out[p] = acc / s;
        }
        return s;
    }

    var DIATONIC = [0, 2, 4, 5, 7, 9, 11];
    /** Largest share of an L1 pitch-class profile that fits ONE diatonic scale (7 notes). */
    function diatonicMass(c) {
        var best = 0, tot = 0, i;
        for (i = 0; i < 12; i++) tot += c[i];
        if (tot <= 0) return 0;
        for (var t = 0; t < 12; t++) {
            var m = 0;
            for (i = 0; i < 7; i++) m += c[(t + DIATONIC[i]) % 12];
            if (m > best) best = m;
        }
        return best / tot;
    }

    /**
     * The bar a singer ENTERS on: the onset of the first real phrase, pushed 0.9
     * beat forward before it is floored to a bar — a pickup on the "and" of the last
     * beat of bar n belongs to bar n+1, the bar the phrase is ABOUT. The same rule on
     * both songs makes the self-mashup land on the exact bar (a fixed "¼ of the bar
     * is sung" threshold flipped by one bar on 5 of 13 songs: a stretched pickup
     * crosses it in a different bar). −1 when nobody sings.
     *
     * A real phrase: ≥ 0.8 s, ≥ 2 s of singing within 8 s of its onset (a breath, a
     * count-in or separation bleed at 0:00 is not an entrance), and not an ISOLATED
     * FRAGMENT — shorter than this singer's median phrase AND followed by ≥ 4 s of
     * silence (and more than twice their median gap). An intro ad-lib of 1.08 s, 6 s
     * before 2.8-s lines that follow each other 0.6 s apart, became the entrance on
     * one copy of a song and not on its transposed twin, where it measured 0.7 s
     * (7 s off). Both halves are needed, measured on the 13 self-mash songs
     * (23.09.2026): a gap rule alone skipped a rapper's first 1.95-s line (their
     * lines are 1.3 s, 5 s apart — 58 s off), a length rule alone skipped the first
     * 2.4-s line of a ballad whose median line is 4.4 s.
     */
    function entryBar(phrases, grid) {
        if (!phrases || !phrases.length) return -1;
        var bars = barsOf(grid);
        if (bars.length < 2) return -1;
        var beatSec = grid.bpm > 0 ? 60 / grid.bpm : 0.5;
        var lens = [], gaps = [], k;
        for (k = 0; k < phrases.length; k++) {
            lens.push(phrases[k].t1 - phrases[k].t0);
            if (k) gaps.push(phrases[k].t0 - phrases[k - 1].t1);
        }
        var num = function (a, b) { return a - b; };
        lens.sort(num); gaps.sort(num);
        var medLen = lens[lens.length >> 1], medGap = gaps.length ? gaps[gaps.length >> 1] : 0;
        for (var i = 0; i < phrases.length; i++) {
            var ph = phrases[i];
            if (ph.t1 - ph.t0 < 0.8) continue;
            var after = i + 1 < phrases.length ? phrases[i + 1].t0 - ph.t1 : Infinity;
            if (ph.t1 - ph.t0 < medLen && after >= Math.max(4, 2 * medGap)) continue;
            var sung = 0;
            for (var j = i; j < phrases.length && phrases[j].t0 < ph.t0 + 8; j++) sung += Math.min(phrases[j].t1, ph.t0 + 8) - phrases[j].t0;
            if (sung < 2) continue;
            var t = ph.t0 + 0.9 * beatSec, b = 0;
            while (b + 1 < bars.length && bars[b + 1].t <= t) b++;
            return b;
        }
        return -1;
    }

    /** Tempo multiplier k (A-beat = k B-beats) and the resulting stretch. */
    function tempoMatch(bpmA, bpmB) {
        var best = null;
        [0.5, 1, 2].forEach(function (k) {
            var stretch = k * bpmA / bpmB; // >1 → the vocal must be slowed down
            var c = Math.abs(Math.log(stretch));
            if (!best || c < best.cost) best = { k: k, stretch: stretch, cost: c };
        });
        return best;
    }

    /** Cheapest split of a total relative shift s between vocal (v) and pitched instrumental stems (i): v − i = s. */
    function splitShift(s, policy) {
        policy = policy || 'auto';
        if (policy === 'vocal' || s === 0) return { vocal: s, inst: 0 };
        if (policy === 'music') return { vocal: 0, inst: -s };
        var best = null;
        for (var v = -6; v <= 6; v++) {
            var i = v - s;
            if (i < -6 || i > 6) continue;
            var cost = 0.6 * Math.abs(v) + (Math.abs(v) > 2 ? 3 * (Math.abs(v) - 2) : 0) + 0.8 * Math.abs(i) + (Math.abs(i) > 3 ? 2 * (Math.abs(i) - 3) : 0);
            if (!best || cost < best.cost - 1e-9) best = { vocal: v, inst: i, cost: cost };
        }
        return { vocal: best.vocal, inst: best.inst };
    }
    function shiftCost(s) { var d = splitShift(s, 'auto'); return 0.6 * Math.abs(d.vocal) + 0.8 * Math.abs(d.inst); }

    /** Bar starts (seconds) and indices from a grid. */
    function barsOf(grid) {
        var out = [];
        for (var i = 0; i < grid.downIdx.length; i++) out.push({ beat: grid.downIdx[i], t: grid.beats[grid.downIdx[i]] });
        return out;
    }

    // ── the music under a vocal that outlasts it ───────────────────────────────────────────
    // Bug report 2026-10-06 («only 3–4 words of my a cappella made it into the mashup»): nothing decided
    // what happens when the vocal, from its landing, is longer than the music. The vocal ran on alone
    // after the music had ended — 212 of 561 two-song builds of three weeks (RU, tool_complete), a
    // quarter of the vocal or more in 108 of them — or, where the music was several times shorter, the
    // landing score (`cover`) picked the LAST phrase group and dropped the rest of the vocal (a 173-s
    // a cappella on a 49-s beat kept its last 14 s). What a producer does instead: the beat goes round
    // again under the verse, in whole phrases. So does this.

    /** The music's grid as far as it reaches: `j1` — the beat index of the end of its last WHOLE bar
     *  (a virtual downbeat when the file ends right on a bar line), `beatT(j)` extrapolating past the
     *  last beat, `endBeat` — the file's end in beats. */
    function musicReach(grid, duration) {
        var beats = grid.beats, nb = beats.length, bars = barsOf(grid), nB = bars.length;
        var beatLen = nb > 1 ? (beats[nb - 1] - beats[0]) / (nb - 1) : (grid.bpm > 0 ? 60 / grid.bpm : 0.5);
        var beatT = function (j) { return j < nb ? beats[j] : beats[nb - 1] + (j - (nb - 1)) * beatLen; };
        var meter = grid.meter || 4, lastEnd = nB ? bars[nB - 1].beat + meter : 0;
        // the last bar is whole when the file still sounds at its end (a cut piece usually stops mid-bar)
        var nFull = nB && beatT(lastEnd) <= duration + 1e-3 ? nB : Math.max(0, nB - 1);
        return { bars: bars, nB: nB, nFull: nFull, beatT: beatT, beatLen: beatLen, meter: meter,
                 endBeat: (nb - 1) + Math.max(0, duration - beats[nb - 1]) / beatLen,
                 barBeat: function (b) { return b < nB ? bars[b].beat : bars[nB - 1].beat + (b - nB + 1) * meter; } };
    }

    /**
     * The bars that go round: from the landing bar `o`, as many WHOLE 4-bar phrases as the music has
     * after it (a beat loops seamlessly on a phrase — its pattern is 1, 2 or 4 bars long); fewer than
     * four bars left — the last phrase of the music; a piece shorter than a phrase — all of it. The
     * part after the body (the song's ending) plays once, after the repeats. `nEnd` — the bar the body
     * may not pass (a fade-out is not looped). null when nothing can go round under `o`.
     */
    function loopBody(nEnd, o) {
        var avail = nEnd - o, L0, L1;
        if (avail >= 4) { L0 = o; L1 = o + 4 * Math.floor(avail / 4); }
        else if (nEnd >= 4) { L1 = nEnd; L0 = nEnd - 4; }
        else if (nEnd >= 1) { L0 = 0; L1 = nEnd; }
        else return null;
        return o < L1 ? [L0, L1] : null;
    }

    /**
     * How the music goes on under a vocal that ends at B-beat `needBeat` (the vocal landed on bar `o`).
     * null — the music already reaches it. Otherwise the body goes round in whole 4-bar phrases until
     * the vocal has ended — the last pass may stop part-way, on a phrase line — and then the rest of the
     * file plays once (a song's ending comes AFTER the singer, as it did in the song). Returned, in B's
     * own seconds: `segs` [{at, from, dur}], the grid the vocal is warped onto (`beats`, `downIdx` — the
     * repeated bars are real bars of it), the new `duration`; `l0`/`l1` (bars) and `n` (passes) for the
     * page; `insertBeat`/`inserted` — where the repeats go into the grid and how many beats they are.
     * `opts.level` — per-bar level (dB) of the music: a quiet tail (a fade-out) stays out of the body.
     */
    function musicLoop(grid, duration, o, needBeat, opts) {
        opts = opts || {};
        if (!grid || !grid.beats || grid.beats.length < 2) return null;
        var R = musicReach(grid, duration);
        // `opts.slack` — how many beats past the music's end the vocal may run without sending it round
        if (R.nB < 2 || !(needBeat > R.endBeat + (opts.slack || 0) + 1e-6)) return null;
        var nEnd = R.nFull, lv = opts.level;
        if (lv && lv.length >= R.nFull && R.nFull - o > 4) {
            var part = [];
            for (var q = Math.max(0, o); q < R.nFull; q++) part.push(lv[q]);
            part.sort(function (a, b) { return a - b; });
            var med = part[part.length >> 1];
            while (nEnd - 1 >= o + 4 && lv[nEnd - 1] < med - 9) nEnd--;
        }
        var body = loopBody(nEnd, o) || loopBody(R.nFull, o);
        if (!body) return null;
        var L0 = body[0], L1 = body[1], j0 = R.barBeat(L0), j1 = R.barBeat(L1), nb = grid.beats.length;
        var T0 = R.beatT(j0), T1 = Math.min(duration, R.beatT(j1)), D = T1 - T0, bodyBeats = j1 - j0;
        if (!(D > 0) || bodyBeats < 1) return null;
        // the beats that go in: whole phrases (or the whole piece, when the body is shorter than one)
        // until the vocal has ended — counted from the body's end, so the ending follows the singer
        var phrase = Math.min(bodyBeats, 4 * R.meter);
        var ins = Math.min(500 * bodyBeats, Math.max(phrase, Math.ceil((needBeat - j1) / phrase - 1e-9) * phrase));
        var full = Math.floor(ins / bodyBeats), rest = ins - full * bodyBeats;
        var restDur = rest ? R.beatT(j0 + rest) - T0 : 0, added = full * D + restDur;
        var beats = [], down = [], isDown = {}, j, k, d;
        for (d = 0; d < grid.downIdx.length; d++) isDown[grid.downIdx[d]] = true;
        if (R.barBeat(R.nB) === j1 || j1 >= nb) isDown[j1] = true;
        for (j = 0; j < j1; j++) { beats.push(R.beatT(j)); if (isDown[j]) down.push(j); }
        for (k = 0; k < ins; k++) {
            var pass = Math.floor(k / bodyBeats), w = k - pass * bodyBeats;
            if (isDown[j0 + w]) down.push(j1 + k);
            beats.push(T1 + pass * D + (R.beatT(j0 + w) - T0));
        }
        for (j = j1; j < nb; j++) { beats.push(grid.beats[j] + added); if (isDown[j]) down.push(j + ins); }
        var segs = [{ at: 0, from: 0, dur: T1 }], at = T1;
        for (k = 0; k < full; k++) { segs.push({ at: at, from: T0, dur: D }); at += D; }
        if (rest) { segs.push({ at: at, from: T0, dur: restDur }); at += restDur; }
        if (duration - T1 > 1e-3) segs.push({ at: at, from: T1, dur: duration - T1 });
        return { l0: L0, l1: L1, n: full + (rest ? 1 : 0), t0: T0, t1: T1, bodyBeats: bodyBeats, insertBeat: j1, inserted: ins, segs: segs,
                 beats: Float64Array.from(beats), downIdx: Int32Array.from(down), duration: duration + added,
                 meter: grid.meter, bpm: grid.bpm };
    }

    /**
     * Per-bar level (dB) of a mono signal against a bar list, and a "rise"
     * score per bar: mean level of the 4 bars from here minus the 4 before.
     */
    function barRise(rmsFrames, frameSec, bars) {
        var lv = [];
        for (var b = 0; b < bars.length; b++) {
            var t0 = bars[b].t, t1 = b + 1 < bars.length ? bars[b + 1].t : t0 + (bars[1] ? bars[1].t - bars[0].t : 2);
            var f0 = Math.floor(t0 / frameSec), f1 = Math.max(f0 + 1, Math.floor(t1 / frameSec)), acc = 0, c = 0;
            for (var f = f0; f < f1 && f < rmsFrames.length; f++) { acc += rmsFrames[f] * rmsFrames[f]; c++; }
            lv.push(10 * Math.log10((c ? acc / c : 0) + 1e-12));
        }
        var rise = lv.map(function (_, b) {
            var a = 0, na = 0, z = 0, nz = 0;
            for (var j = b; j < b + 4 && j < lv.length; j++) { a += lv[j]; na++; }
            for (j = b - 4; j < b; j++) if (j >= 0) { z += lv[j]; nz++; }
            return (na && nz) ? a / na - z / nz : 0;
        });
        return { level: lv, rise: rise };
    }

    /**
     * The plan.
     *
     * A = { grid, vocal:{data,times} (pitch-class frames of the sung melody), phrases:[{t0,t1}],
     *       voicedRatio (0…1), tuningCents, duration,
     *       whole (the file IS the vocal — an a cappella), sound:{t0,t1,sung0} (soundSpan of the track) }
     * B = { grid, harm:{data,times} (bass+other chroma, MashupDSP.mixChroma), rms:{data, frameSec},
     *       phrases:[{t0,t1}] (B's own vocal, if any), tuningCents, duration }
     * opts = { tempoMode:'b'|'a'|'mid'|'custom', bpm, shiftPolicy:'auto'|'vocal'|'music',
     *          forceShift (int|null), forceBar (int|null), forceRegion:[a0,a1]|null, maxBars }
     *
     * Two decisions, taken separately because they fail in different ways:
     *  1. THE SHIFT — from the whole sung region at once against the whole
     *     instrumental (keyScores): z-scored single-scale mass plus a quarter of the
     *     z-scored consonance field (numbers at keyScores). The first tuning of this
     *     sum (56/56 on synthetic pairs, "any per-beat term lowered it") was measured on
     *     synthetic vocals whose vibrato drifted up to ±1.4 semitones inside a note —
     *     that conclusion is void and per-beat terms are untested on clean material.
     *  2. THE LANDING — which bar of the instrumental the region starts on:
     *     4-bar phrase starts, a rise in level (the verse after an intro), and the
     *     per-beat consonance of THAT shift on THOSE beats.
     */
    var EARLY_S = 30;
    function plan(A, B, opts) {
        opts = opts || {};
        var W = opts.weights || {};
        var tm = tempoMatch(A.grid.bpm, B.grid.bpm), k = tm.k;
        var barsA = barsOf(A.grid), barsB = barsOf(B.grid);
        var MA = A.grid.meter, MB = B.grid.meter;
        var meterOk = Math.abs(MA * k - MB) < 1e-6;
        var barRatio = (MA * k) / MB;
        if (barsA.length < 2 || barsB.length < 2) return null;

        // ---- the sung region of A, in A bars
        var phr = A.phrases || [];
        var firstT = phr.length ? phr[0].t0 : 0, lastT = phr.length ? phr[phr.length - 1].t1 : A.duration;
        function barIdxAt(bars, t) { var i = 0; while (i + 1 < bars.length && bars[i + 1].t <= t + 1e-6) i++; return i; }
        var fsA = entryBar(phr, A.grid);
        var a0Full = fsA >= 0 ? fsA : barIdxAt(barsA, firstT), a1Full = Math.min(barsA.length, barIdxAt(barsA, lastT) + 1);
        var barSecA = barsA[1].t - barsA[0].t;
        var starts = [a0Full];
        for (var p = 1; p < phr.length; p++) {
            var gapBars = (phr[p].t0 - phr[p - 1].t1) / barSecA;
            var bi = barIdxAt(barsA, phr[p].t0);
            if (gapBars >= 0.9 && starts.indexOf(bi) < 0 && bi < a1Full - 2) starts.push(bi);
        }
        var nB = barsB.length;
        if (opts.forceRegion) starts = [opts.forceRegion[0]];
        // the landing the person chose: bar `forceBar` and `forceBeat` B-beats from it, as the bar it falls in
        // (landing: «+4 beats» IS «+1 bar»). Beats, not seconds, because the music's beats are what the vocal
        // is warped onto: a shift by n beats as TIME (n × the mean beat) missed the music's n-th beat by more
        // than 20 ms at the 90th percentile on 5 of 15 corpus songs with live tempo, by up to 103 ms for one
        // beat and 231 ms for eight (18.10.2026, tests/scratch/mm-beatshift-drift-scratch.mjs)
        var fLand = opts.forceBar != null ? landing(barsB, opts.forceBar, opts.forceBeat || 0) : null;

        var tmp = new Float64Array(12);
        function vocalSlots(a0, a1) {
            var aBeat0 = barsA[a0].beat;
            var aBeatEnd = a1 < barsA.length ? barsA[a1].beat : A.grid.beats.length - 1;
            var units = Math.max(1, Math.round((aBeatEnd - aBeat0) * k));
            var sl = new Float32Array(units * 12), w = new Float32Array(units);
            for (var u = 0; u < units; u++) {
                var t0 = timeAtBeat(A.grid.beats, aBeat0 + u / k), t1 = timeAtBeat(A.grid.beats, aBeat0 + (u + 1) / k);
                chromaIn(A.vocal, t0, t1, tmp);
                var e = 0, d;
                for (d = 0; d < 12; d++) e += tmp[d];
                for (d = 0; d < 12; d++) sl[u * 12 + d] = e > 0 ? tmp[d] / e : 0;
                w[u] = e;
            }
            var ws = Array.from(w).filter(function (x) { return x > 0; }).sort(function (a, b) { return a - b; });
            var cap = ws.length ? ws[Math.floor(ws.length * 0.9)] : 1, floor = ws.length ? ws[Math.floor(ws.length * 0.2)] * 0.25 : 0;
            for (u = 0; u < units; u++) w[u] = w[u] <= floor ? 0 : Math.min(1, w[u] / (cap || 1));
            return { pc: sl, w: w, units: units, aBeat0: aBeat0, pitched: ws.length > 0 };
        }
        // the instrumental: one consonance field per B-beat, and its whole-song profile
        var nBeatsB = B.grid.beats.length, bField = new Float32Array(nBeatsB * 12), fld = new Float64Array(12);
        var globalB = new Float64Array(12);
        for (var j = 0; j + 1 < nBeatsB; j++) {
            chromaIn(B.harm, B.grid.beats[j], B.grid.beats[j + 1], tmp);
            var tsum = 0, d3;
            for (d3 = 0; d3 < 12; d3++) tsum += tmp[d3];
            field(tmp, fld);
            for (d3 = 0; d3 < 12; d3++) { bField[j * 12 + d3] = fld[d3]; globalB[d3] += tsum > 0 ? tmp[d3] / tsum : 0; }
        }
        var rise = B.rms ? barRise(B.rms.data, B.rms.frameSec, barsB).rise : barsB.map(function () { return 0; });
        var reach = musicReach(B.grid, B.duration || B.grid.beats[B.grid.beats.length - 1]);

        var tuneShift = 0;
        if (isFinite(A.tuningCents) && isFinite(B.tuningCents)) {
            var dc = B.tuningCents - A.tuningCents;
            if (Math.abs(dc) >= 10) tuneShift = Math.round(dc) / 100;
        }
        var rap = A.voicedRatio != null && A.voicedRatio < (W.rapBelow != null ? W.rapBelow : 0.3);
        // where the instrumental's OWN singer came in: the natural landing for the new one
        // (verse over verse — what a mashup producer does by hand). Absent for an instrumental.
        var bEntry = (B.phrases && B.phrases.length) ? entryBar(B.phrases, B.grid) : -1;

        var best = null, alts = [], cands = [];
        for (var si = 0; si < starts.length; si++) {
            var a0 = starts[si];
            var a1 = opts.forceRegion ? opts.forceRegion[1] : a1Full;
            if (a1 - a0 < 1) continue;
            var V = vocalSlots(a0, a1);
            // what of the vocal is heard for these bars, and how far it reaches either side of the
            // landing in B-beats: `headX` — what is SUNG before bar a0 beyond the one bar a pickup may
            // take (an a cappella's lines before the bar its singer «comes in» on — sungFrom();
            // the hiss before them may fall before the music and not be heard), `vReach` — to its
            // last sound
            var X = vocalExtent(A, barsA, a0, a1, a0 === a0Full && a1 === a1Full);
            var sungT = sungFrom(A);
            sungT = sungT != null ? Math.max(X.t0, sungT - PAD_IN) : X.t0;
            var headU = (barsA[a0].beat - beatAtTime(A.grid.beats, sungT)) * k, headX = headU > MB + 0.5 ? headU - MB : 0;
            var vReach = (beatAtTime(A.grid.beats, X.t1) - barsA[a0].beat) * k;
            // ---- 1. the shift, from the whole region
            var gv = new Float64Array(12), q0;
            for (var u0 = 0; u0 < V.units; u0++) for (q0 = 0; q0 < 12; q0++) gv[q0] += V.w[u0] * V.pc[u0 * 12 + q0];
            var ks = keyScores(gv, globalB, W.wField);
            var shift = 0;
            if (opts.forceShift != null) shift = opts.forceShift;
            else if (!rap) shift = pickShift(ks.combined, W.tieZ);
            var sIdx = ((shift % 12) + 12) % 12;
            // ---- 2. the landing
            // every landing bar is a candidate. A region longer than what is left of the music
            // runs on over the music going round (musicLoop): its harmony is scored on the bars
            // that repeat, and `cover` — the share of the vocal over the music as written — keeps
            // the landing where the vocal fits when it can. The length is counted in B-BEATS
            // (V.units), not A-bars — in half/double time the A bars may be found every 3 beats
            // (Suno test song: 332 beats, 104 "bars"), and an A-bar length made the whole vocal
            // "too long" and pinned it to bar 0.
            var oMax = nB - 1;
            for (var o = 0; o <= oMax; o++) {
                if (fLand && o !== fLand.o) continue;
                var db = fLand ? fLand.db : 0, bBeat0 = (barsB[o] ? barsB[o].beat : 0) + db;
                // the bars after the body are repeats only when the vocal outlasts the music from here
                var lb = bBeat0 + V.units > reach.endBeat + 1e-6 ? loopBody(reach.nFull, o) : null;
                var loc = 0, wsum = 0, lj0 = lb ? reach.barBeat(lb[0]) : 0, lj1 = lb ? reach.barBeat(lb[1]) : 0;
                for (var u = 0; u < V.units; u++) {
                    var wu = V.w[u];
                    if (!wu) continue;
                    var jb = bBeat0 + u;
                    if (lb && jb >= lj1) jb = lj0 + (jb - lj1) % (lj1 - lj0);
                    if (jb >= nBeatsB - 1) continue;
                    var dot = 0;
                    for (var q = 0; q < 12; q++) dot += V.pc[u * 12 + q] * bField[jb * 12 + (q + sIdx) % 12];
                    loc += wu * dot; wsum += wu;
                }
                // no beat of the vocal over the music from this bar: not a landing. A vocal with no
                // readable note anywhere (whispered, spoken, drowned in an effect) is still landed —
                // by the bars alone; it used to leave no candidate and the build ended «no plan»
                if (wsum <= 0 && V.pitched) continue;
                loc = wsum > 0 ? loc / wsum : 0;
                // phrases are counted from the instrumental singer's entrance, not from bar 0
                // of the file (bar 0 is often a pickup or a lone intro bar)
                var rel = bEntry >= 0 ? o - bEntry : o;
                var phrase = ((rel % 4 + 4) % 4 === 0) ? 1 : ((rel % 2 + 2) % 2 === 0 ? 0.3 : 0);
                var riseB = Math.max(0, Math.min(1, (rise[o] || 0) / 6));
                var cover = Math.min(1, (nBeatsB - 1 - bBeat0) / Math.max(1, V.units));
                // a landing that would leave the vocal's beginning before the music's first beat (an
                // a cappella file with lines before the bar its singer «comes in» on) is taken only
                // when the person asks for that bar, or when no other bar exists
                var headMiss = headX > bBeat0 + 1e-6 ? 1 : 0;
                // the entrance is SHARP: its own bar, then the same bar one phrase (4/8/16 bars)
                // away — never "roughly there", which let a bar-0 phrase bonus pull it off by one
                var entry = 0;
                if (bEntry >= 0) {
                    var de = Math.abs(o - bEntry);
                    entry = de === 0 ? 1 : (de % 4 === 0 && de <= 16 ? 0.5 : 0.15 * Math.exp(-(de / 8) * (de / 8)));
                }
                var first = a0 === a0Full ? 1 : 0;
                // Music with NO singer of its own (a minus, a beat, an instrumental) has no entrance
                // to land on, and the terms left — the noisy per-beat harmony, a phrase line, a rise —
                // are met anywhere in the track: a 13-s a cappella landed 4:27 into a 5-min beat.
                // Field, 469 builds on such music (RU, 25.09–14.10.2026): where the default landed 30 s
                // or deeper (100), 58 people moved the vocal, 51 of them EARLIER (to a median of 24 s);
                // where it landed earlier, moves went both ways evenly. And singers come in early: on
                // the 19 corpus songs at a median of 6.4 s, 15 within 16 s — by no rule of the music's
                // level (the bar its body begins on is the singer's bar on 2 of 19; 6 sing before it).
                // So here the landing leans to the beginning: the terms above choose among the first
                // bars, and a later bar has to be worth its distance.
                var early = bEntry < 0 ? Math.min(1, ((barsB[o] ? barsB[o].t : 0) - barsB[0].t) / EARLY_S) : 0;
                // one song's stems (the engine gave the singer the music's own grid): the bar the
                // vocal was sung on is the only landing there is
                var twin = A.grid === B.grid && o === a0 ? 1 : 0;
                // Harmony only REFINES the landing: on real songs the per-beat agreement of a
                // melody with chords is noisy (a short late fragment out-scored the whole vocal
                // on 2 of 6 coherent test songs), while "first phrase onto the instrumental
                // singer's entrance" was exact on all of them.
                var sc = (W.wLocal != null ? W.wLocal : 0.3) * loc + 0.05 * phrase + (bEntry >= 0 ? 0.03 : 0.08) * riseB + 0.3 * cover
                    + (W.wEntry != null ? W.wEntry : 0.4) * entry + (W.wFirst != null ? W.wFirst : 0.25) * first
                    - (W.wEarly != null ? W.wEarly : 0.3) * early - headMiss + 2 * twin;
                cands.push({ a0: a0, a1: a1, o: o, db: db, shift: shift, local: loc, score: sc, cover: cover, entry: entry, first: first, phrase: phrase, key: ks, V: V, X: X, vReach: vReach });
            }
        }
        if (!cands.length) return null;
        cands.sort(function (x, y) { return y.score - x.score; });
        // the WHOLE vocal unless the person chose a part: a later phrase group is an alternative, never
        // the default — once the music goes round under a long vocal, nothing has to be dropped to fit,
        // and the fit term alone used to hand the build the singer's last few lines (report 2026-10-06)
        best = cands.filter(function (c) { return c.first; })[0] || cands[0];
        for (var r = 0; r < cands.length && alts.length < 3; r++) {
            var c = cands[r];
            if (c === best) continue;
            if ([best].concat(alts).every(function (z) { return z.a0 !== c.a0 || Math.abs(z.o - c.o) >= 4; })) alts.push(c);
        }
        var split = splitShift(best.shift, opts.shiftPolicy);
        var byShift = [];
        for (var s2 = -5; s2 <= 6; s2++) byShift.push({ shift: s2, score: best.key.combined[(s2 + 12) % 12] });

        var bpmOut = B.grid.bpm;
        // k counts B-beats per A-beat (tempoMatch: stretch = k·bpmA/bpmB), so the vocal's own tempo on
        // the music's beat level is bpmA·k. Until 2026-09-24 both modes DIVIDED by k: a 60-BPM count
        // of a 120-BPM a cappella over a 120-BPM track gave «Tempo: vocal» 30 BPM and «middle» 60.
        if (opts.tempoMode === 'a') bpmOut = A.grid.bpm * k;
        else if (opts.tempoMode === 'mid') bpmOut = Math.sqrt(B.grid.bpm * A.grid.bpm * k);
        else if (opts.tempoMode === 'custom' && opts.bpm > 30) bpmOut = opts.bpm;

        return {
            k: k, stretch: tm.stretch, meterA: MA, meterB: MB, meterOk: meterOk, barRatio: barRatio,
            bpmA: A.grid.bpm, bpmB: B.grid.bpm, bpmOut: Math.round(bpmOut * 100) / 100,
            tempoMode: opts.tempoMode || 'b', rap: rap,
            // `db` — B-beats from bar o's line to where the vocal's bar a0 lands (0 unless the person moved
            // it by beats; negative only before the music's first bar)
            a0: best.a0, a1: best.a1, o: best.o, db: best.db, shift: best.shift, units: best.V.units, nBarsA: barsA.length,
            // what of the singer's track is heard (its own seconds) and how many B-beats after the
            // landing its last sound comes; `full` — the whole vocal, not a fragment the person chose
            extent: best.X, reach: best.vReach, full: best.a0 === a0Full && best.a1 === a1Full,
            vocalShift: split.vocal + tuneShift, instShift: split.inst, tuneShift: tuneShift,
            harmony: best.key.fit[(best.shift % 12 + 12) % 12], local: best.local, score: best.score, cover: best.cover,
            byShift: byShift,
            debugTop: opts.debug ? cands.slice(0, 6).map(function (c) { return { a0: c.a0, a1: c.a1, o: c.o, loc: +c.local.toFixed(3), cover: +c.cover.toFixed(2), entry: +c.entry.toFixed(2), first: c.first, sc: +c.score.toFixed(3) }; }) : undefined,
            bEntry: bEntry, a0Full: a0Full, a1Full: a1Full, starts: starts,
            alternatives: alts.map(function (z) { return { a0: z.a0, a1: z.a1, o: z.o, shift: z.shift, local: z.local }; }),
            regionA: { t0: barsA[best.a0].t, t1: best.a1 < barsA.length ? barsA[best.a1].t : A.duration },
            landingB: { bar: best.o, beat: best.db, t: barsB[best.o] ? timeAtBeat(B.grid.beats, barsB[best.o].beat + best.db) : 0 }
        };
    }

    /**
     * Bar o of the music and `db` beats from its line, as the bar that beat falls in and the beats into
     * it: { o, db }. Before the first bar the beats stay negative (a vocal may begin before the music's
     * first bar line: the whole mashup then starts later — MashupEngine leadOf).
     */
    function landing(barsB, o, db) {
        var n = barsB.length;
        if (!n) return { o: 0, db: 0 };
        o = Math.max(0, Math.min(n - 1, Math.round(o || 0)));
        var j = barsB[o].beat + Math.round(db || 0), i = 0;
        while (i + 1 < n && barsB[i + 1].beat <= j) i++;
        return { o: i, db: j - barsB[i].beat };
    }

    /**
     * Where a drag of the vocal by `dt` seconds of the timeline takes it, by the snap the person chose:
     *   'bar'  — whole bars; the beats and the fine nudge it already has stay;
     *   'beat' — whole beats; the fine nudge stays;
     *   'free' — anywhere: whole beats (exact on the music's beats) plus a fine nudge within half a beat.
     * o = { beatSec, meter, fine } — the mashup's beat, the music's meter, the current nudge in seconds.
     * → { bars, beats, fine }. Every DAW and iMashup offer the three (competitor survey, 18.10.2026);
     * snapping by bars alone left a vocal that sat a beat off with no way to it but the keyboard.
     */
    function dragTarget(dt, snap, o) {
        var b = o.beatSec, m = o.meter || 4, fine = o.fine || 0;
        if (!(b > 0) || !isFinite(dt)) return { bars: 0, beats: 0, fine: fine };
        if (snap === 'beat') return { bars: 0, beats: Math.round(dt / b), fine: fine };
        if (snap === 'free') { var tot = dt + fine, n = Math.round(tot / b); return { bars: 0, beats: n, fine: tot - n * b }; }
        return { bars: Math.round(dt / (b * m)), beats: 0, fine: fine };
    }

    /**
     * The vocal trimmed at an edge (0 — its start, 1 — its end) by `dt` seconds of the timeline, in whole
     * bars of the SINGER's song — one A-bar lasts meterA·k B-beats there. What is left keeps sounding where
     * it sounded: cutting the start moves the landing on by the beats cut. o = { beatSec, meterA, k, a0, a1,
     * nA } → { a0, a1, beats }.
     */
    function trimTarget(edge, dt, o) {
        var per = o.meterA * o.k, barSec = o.beatSec * per, n = barSec > 0 && isFinite(dt) ? Math.round(dt / barSec) : 0;
        if (edge === 0) { var a0 = Math.max(0, Math.min(o.a1 - 1, o.a0 + n)); return { a0: a0, a1: o.a1, beats: Math.round((a0 - o.a0) * per) }; }
        return { a0: o.a0, a1: Math.max(o.a0 + 1, Math.min(o.nA, o.a1 + n)), beats: 0 };
    }

    /**
     * Whole-region key agreement per shift s (vocal moved UP s semitones):
     *   field[s]    — consonance of the singer's notes against the band's profile,
     *   scale[s]    — share of (singer + band) that fits one diatonic scale,
     *   combined[s] — z(scale) + wField·z(field): the ranking the shift is picked from,
     *   fit[s]      — 0…1 display value (scale mass) for the UI.
     *
     * wField = 0.25, measured 23.09.2026 on in-tune material (the first tuning, 1:1, was
     * done on synthetic vocals whose vibrato drifted ±1.4 semitones within a note):
     * 448 synthetic pairs with known keys (8/12/16/24 bars, two melody generators) —
     * 1:1 407, 0.25 434, every set of 12+ bars 56/56; 118 real 7-s MUSDB18 clips, the
     * song's own vocal over its own accompaniment (pure argmax, no tie-break) — 41 vs 40
     * (±5 is noise at n=118; seven seconds rarely pins the key, see the fifth trap below);
     * six coherent full songs of the corpus — 6/6 both. The misses left are the fifth:
     * a melody without its 4th (or 7th) fits the key a fifth away as well as its own.
     */
    function keyScores(vocalProfile, bandProfile, wField) {
        var wf = wField != null ? wField : 0.25;
        var gSum = 0, bSum = 0, i, s;
        for (i = 0; i < 12; i++) { gSum += vocalProfile[i]; bSum += bandProfile[i]; }
        var bandField = new Float64Array(12); field(bandProfile, bandField);
        var f = new Float64Array(12), m = new Float64Array(12);
        for (s = 0; s < 12; s++) {
            var acc = 0, comb = new Float64Array(12);
            for (i = 0; i < 12; i++) {
                var v = gSum > 0 ? vocalProfile[i] / gSum : 0;
                acc += v * bandField[(i + s) % 12];
                comb[(i + s) % 12] += v;
                comb[i] += bSum > 0 ? bandProfile[i] / bSum : 0;
            }
            f[s] = acc; m[s] = diatonicMass(comb);
        }
        function z(a) { var mu = 0, v2 = 0, q; for (q = 0; q < 12; q++) mu += a[q]; mu /= 12; for (q = 0; q < 12; q++) v2 += (a[q] - mu) * (a[q] - mu); v2 = Math.sqrt(v2 / 12) || 1; return Array.from(a, function (x) { return (x - mu) / v2; }); }
        var zf = z(f), zm = z(m), comb2 = new Float64Array(12);
        for (s = 0; s < 12; s++) comb2[s] = zm[s] + wf * zf[s];
        return { field: f, scale: m, combined: comb2, fit: m };
    }

    /**
     * argmax of a 12-entry score; a TRUE tie (within 0.01 z) goes to the smaller |shift|.
     * A wider window is a bias, not a tie-break: 448 synthetic pairs 434 at 0.01, 430 at
     * 0.02, 419 at 0.1 (23.09.2026).
     */
    function pickShift(combined, tieZ) {
        var tie = tieZ != null ? tieZ : 0.01, best = -Infinity, i;
        for (i = 0; i < 12; i++) if (combined[i] > best) best = combined[i];
        var pick = 0, pickAbs = 99;
        for (i = 0; i < 12; i++) {
            if (combined[i] < best - tie) continue;
            var st = i > 6 ? i - 12 : i, a = Math.abs(st);
            if (a < pickAbs) { pickAbs = a; pick = st; }
        }
        return pick;
    }

    /**
     * Warp anchors for the vocal: [aTimeSec, outTimeSec] per A-beat of the region
     * (and one bar of lead-in before it), onto the output timeline. outGrid is
     * the instrumental's beats AFTER its own tempo change (identical to B's
     * beats when tempoMode = 'b').
     */
    function warpAnchors(pl, A, outBeats, barsB) {
        var barsA = barsOf(A.grid), beats = A.grid.beats, nA = beats.length;
        // `db`: the vocal moved by whole beats lands on the music's beat that many beats on — through the warp,
        // so a live drummer's drift is followed beat by beat (plan: fLand)
        var aBeat0 = barsA[pl.a0].beat, bBeat0 = (barsB[pl.o] ? barsB[pl.o].beat : 0) + (pl.db || 0);
        // The vocal is heard from extent.t0 to extent.t1 (vocalExtent), and ALL of that is laid onto
        // the music's beats: A-beat x ↦ B-beat bBeat0 + (x − aBeat0)·k, on either side of the landing
        // and past either end of A's own grid (both grids go on at their last beat's length). The
        // first and the last anchor ARE the ends of what is heard — the engine crops between them.
        // (Until 2026-10-14 the anchors ran from one bar before bar a0 to the grid's last beat and the
        // crop took three more seconds after it, unwarped: a grid that ended before the singing did —
        // a model grid on a voice stops where the model stops hearing beats — cut the ending.)
        var X = pl.extent || { t0: aBeat0 - A.grid.meter >= 0 ? beats[aBeat0 - A.grid.meter] : 0, t1: beats[nA - 1] };
        var outAt = function (t) { return timeAtBeat(outBeats, bBeat0 + (beatAtTime(beats, t) - aBeat0) * pl.k); };
        var step = pl.k === 0.5 ? 2 : 1; // two A-beats per B-beat → anchor every other A-beat
        // a beat closer to an end than MIN_GAP is the end itself (the stretch refuses a step under 1 ms)
        var MIN_GAP = 0.005, pairs = [[X.t0, outAt(X.t0)]];
        for (var ia = aBeat0 - step * Math.ceil(aBeat0 / step); ia < nA; ia += step) {
            if (ia < 0 || beats[ia] <= X.t0 + MIN_GAP) continue;
            if (beats[ia] >= X.t1 - MIN_GAP) break;
            pairs.push([beats[ia], timeAtBeat(outBeats, bBeat0 + (ia - aBeat0) * pl.k)]);
        }
        pairs.push([X.t1, outAt(X.t1)]);
        // monotone and strictly increasing on both axes; the last pair — the vocal's end — always stays
        var clean = [], last = pairs.length - 1;
        for (var i = 0; i < pairs.length; i++) {
            var c = clean[clean.length - 1];
            if (!c || (pairs[i][0] > c[0] + 2e-3 && pairs[i][1] > c[1] + 2e-3)) clean.push(pairs[i]);
            else if (i === last && clean.length > 1) { clean.pop(); i--; }
        }
        return clean;
    }

    /**
     * The verdict on a PAIR from what the analysis read of each song — tempo, meter, key. ONE owner
     * for the deck strip and the pair finder: a finder ranking by its own rule would call a pair
     * «good» that the strip then calls «hard». good = a stretch the ear misses (≤ 6 %, k ∈ {½,1,2})
     * AND keys that mix on the Camelot wheel (same / relative / adjacent); hard = past 12 % (where
     * lacuna.fm refuses, guides say 8–10 %) or two meters; ok between. `score` orders pairs: the
     * verdict first, then the stretch (1 % ≈ one step of key distance), then the keys.
     */
    var REL_RANK = { same: 0, relative: 1, adjacent: 2, energy: 3, far: 5 };
    function pairFit(a, b) {
        var tm = tempoMatch(a.bpm, b.bpm), pct = Math.abs(tm.stretch - 1);
        var rel = a.key && b.key ? camelotRelation(a.key, b.key) : null;
        var meterOff = !!(a.meter && b.meter && a.meter * tm.k !== b.meter && a.meter !== b.meter);
        var verdict = pct > 0.12 || meterOff ? 'hard' : (pct <= 0.06 && /^(same|relative|adjacent)$/.test(rel || '') ? 'good' : 'ok');
        var score = { good: 0, ok: 1, hard: 2 }[verdict] * 100 + pct * 100 + (rel ? REL_RANK[rel] : 4);
        return { k: tm.k, stretch: tm.stretch, pct: pct, rel: rel, meterOff: meterOff, verdict: verdict, score: score };
    }

    // ── three songs: a second singer over the same music ────────────────────────────────
    /**
     * The bar right after a singer's fragment, one whole 4-bar phrase count from where they
     * landed — where the second singer takes over. null when fewer than 4 bars of music are left.
     */
    function barAfter(pl, nB) {
        var o = pl.o + Math.ceil((pl.a1 - pl.a0) * (pl.barRatio || 1) / 4 - 1e-9) * 4;
        return o <= nB - 4 ? o : null;
    }
    /**
     * The first singer's region when two singers share the music: the first half of what is left
     * after their landing (whole 4-bar phrases, at least 8 bars). null = no cut: the region fits
     * already, or fewer than 16 bars are left (the second singer is then placed by their own plan,
     * and the person moves them).
     */
    function shareRegion(pl, nB) {
        var after = nB - pl.o;
        if (after < 16) return null;
        var lenB = Math.max(8, Math.floor(after / 2 / 4) * 4);
        var lenA = Math.max(1, Math.round(lenB / (pl.barRatio || 1)));
        return pl.a1 - pl.a0 > lenA ? [pl.a0, pl.a0 + lenA] : null;
    }
    /**
     * The SECOND singer's plan over the same music. The tempo and the band's key are already
     * decided by the first plan `pa`: the tempo is pa.bpmOut (tempoMode 'custom'), and the band has
     * been moved by pa.instShift for the first singer — so the second vocal carries its whole
     * interval to the band AND the band's own move (v − i = s with i fixed: shiftPolicy 'vocal',
     * then vocalShift += pa.instShift). `follow` lands it right after the first singer's fragment;
     * without room there, where its own plan likes. Its fragment is cut to the music left after its
     * landing: a voice never runs on past the end of the music.
     */
    function planSecond(pa, C, B, opts) {
        opts = opts || {};
        var nB = barsOf(B.grid).length;
        var o = Object.assign({}, opts, { shiftPolicy: 'vocal', tempoMode: 'custom', bpm: pa.bpmOut });
        delete o.follow;
        // following the first singer, the second comes in on a bar line: a beat nudge belongs to a place the person chose
        if (opts.follow) { delete o.forceBeat; var ob = barAfter(pa, nB); if (ob != null) o.forceBar = ob; else delete o.forceBar; }
        var p = plan(C, B, o);
        if (!p) return null;
        var room = nB - p.o;
        if ((p.a1 - p.a0) * (p.barRatio || 1) > room + 0.5) {
            var lenC = Math.max(1, Math.floor(room / (p.barRatio || 1)));
            var p2 = plan(C, B, Object.assign({}, o, { forceRegion: [p.a0, Math.min(p.a1, p.a0 + lenC)], forceBar: p.o, forceBeat: p.db }));
            if (p2) { p2.starts = p.starts; p2.a0Full = p.a0Full; p2.a1Full = p.a1Full; p2.alternatives = p.alternatives; p = p2; }
        }
        p.vocalShift += pa.instShift; p.instShift = pa.instShift; p.second = true;
        return p;
    }

    var api = {
        pairFit: pairFit, barAfter: barAfter, shareRegion: shareRegion, planSecond: planSecond, musicLoop: musicLoop, loopBody: loopBody, musicReach: musicReach,
        plan: plan, entryBar: entryBar, keyOf: keyOf, field: field, CONSONANCE: CONSONANCE, diatonicMass: diatonicMass, keyScores: keyScores, pickShift: pickShift, camelotRelation: camelotRelation, vocalPhrases: vocalPhrases,
        soundSpan: soundSpan, vocalExtent: vocalExtent, sungFrom: sungFrom,
        tempoMatch: tempoMatch, splitShift: splitShift, warpAnchors: warpAnchors, landing: landing, dragTarget: dragTarget, trimTarget: trimTarget,
        timeAtBeat: timeAtBeat, beatAtTime: beatAtTime, barsOf: barsOf, barRise: barRise, NOTE: NOTE
    };
    root.MashupPlan = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : globalThis));
